/**
 * Centrální ladicí konstanty světa a renderu.
 * Cokoliv, co ovlivňuje výkon nebo měřítko světa, patří sem — ne roztroušené po kódu.
 */

import { SEA_LEVEL, WORLD_SEED, WORLD_SIZE } from './net/protocol';

export const WORLD = {
  /** Hrana světa v metrech (2 x 2 km). */
  size: WORLD_SIZE,
  /** Rozlišení výškové mapy (počet vzorků na hranu). 1025 => 2 m na texel. */
  heightRes: 1025,
  /** Hrana jednoho chunku v metrech. 2048 / 128 = 16x16 = 256 chunků. */
  chunkSize: 128,
  /** Výška hladiny moře v metrech. */
  seaLevel: SEA_LEVEL,
  /** Seed generátoru — stejný seed = stejný svět na klientu i na serveru. */
  seed: WORLD_SEED,
} as const;

export const CHUNKS_PER_SIDE = WORLD.size / WORLD.chunkSize;
/** Počet metrů na jeden texel výškové mapy. */
export const METERS_PER_TEXEL = WORLD.size / (WORLD.heightRes - 1);

/**
 * Segmenty mřížky pro jednotlivé LOD úrovně.
 * LOD0 = 64 segmentů na 128 m = 2 m na vertex, což přesně odpovídá výškové mapě.
 * Jemnější mřížka by nepřinesla žádný detail navíc, jen trojúhelníky.
 */
export const LOD_SEGMENTS = [64, 32, 16, 8, 4] as const;

/** Vzdálenost (m), za kterou se přepne na další LOD. Délka = LOD_SEGMENTS.length - 1. */
export const LOD_DISTANCES = [180, 380, 700, 1200] as const;

/** Vzhled povrchu — velikosti dlaždic textur a prahy vrstev. */
export const TERRAIN_LOOK = {
  /** Po kolika metrech se textura opakuje. Menší = ostřejší zblízka, ale
   *  dřív viditelné opakování na velké ploše. */
  tileGrass: 5.5,
  tileRock: 9.0,
  tileDirt: 6.5,
  /** Písek má jemné zrno, snese menší dlaždici bez viditelného opakování. */
  tileSand: 4.5,
  tileSnow: 11.0,
  normalStrength: 1.15,
  /** Za touto vzdáleností se normálové mapy vytratí (jinak jen šumí). */
  normalFadeDistance: 260,
  /** Výška pásu pláže nad hladinou. */
  beachHeight: 9,
  snowStart: 175,
  snowFull: 245,
} as const;

/** More. */
export const OCEAN = {
  /** Prstence a segmenty kruhove site: 110 x 128 = zhruba 28 tisic trojuhelniku. */
  rings: 110,
  segments: 128,
  /** Nejmensi a nejvetsi polomer disku v metrech. */
  innerRadius: 0.6,
  outerRadius: 14000,
  /** Amplituda vln v metrech. */
  amplitude: 0.55,
  /** Spicatost hrebenu. 0 = sinusovka, 1 = ostre lamajici se hrebeny. */
  choppiness: 0.82,
  /** Rychlost pohlcovani svetla vodou. Vyssi = kalnejsi, driv temna. */
  absorption: 0.62,
  /** Prah Jacobianu, pod kterym se vlna povazuje za lamajici se. */
  foamCrest: 0.42,
} as const;

/**
 * 3D trava. Pocet instanci = gridSize^2 * bladesPerCell, ale vetsina z nich
 * je zahozena uz podle vzdalenosti drive, nez se sahne na vyskovou mapu.
 */
export const GRASS = {
  enabled: true,
  /** Pocet bunek na hranu pole (270 * 0.32 = 86 m). */
  gridSize: 270,
  /** Velikost bunky v metrech. */
  cellSize: 0.32,
  /** Kolik stebel na bunku v blizkem pasmu (s prirozenym shlukovanim). */
  bladesPerCell: 9,
  /** Segmentu na steblo: 5 => hladsi prirozeny ohyb. */
  segments: 5,
  bladeHeight: 0.38,
  bladeWidth: 0.015,
  /** Od teto vzdalenosti zacina hustota plynule klesat. */
  midDistance: 15,
  /**
   * Kde zacina a konci rozpousteni ditherem.
   *
   * Pri 38 m koncila louka viditelnou carou a za ni zustal holy koberec.
   * Ctyricet dva metru uz prechod schova.
   *
   * Vys uz ne: kreslena plocha roste s druhou mocninou vzdalenosti, takze
   * padesat metru znamena skoro dvojnasobek stebel. Zkousel jsem to a
   * v tomhle prostredi se cena travy nedala zmerit (prohlizec skrtil
   * skryty panel a GPU casovac chybi), takze jsem to nechtel poslat
   * naslepo. Pocet stebel na bunku klesl z 10 na 9, cimz se ten vetsi
   * dosah zhruba zaplati.
   */
  fadeStart: 28,
  fadeEnd: 42,
  windStrength: 0.35,
  windSpeed: 1.0,
} as const;

/** Multiplayer. */
export const NET = {
  /**
   * Adresa Workeru. V dev režimu míří na lokální `wrangler dev`, v produkci
   * se bere z proměnné VITE_SERVER_URL zadané při buildu.
   */
  serverUrl:
    (import.meta.env.VITE_SERVER_URL as string | undefined) ??
    (import.meta.env.DEV ? 'ws://127.0.0.1:8787/ws' : ''),
  /** Jméno zóny. Jeden Durable Object na zónu. */
  zone: 'start',
  /** Když je vypnuto, hra běží čistě lokálně a nikam se nepřipojuje. */
  enabled: true,
} as const;

export const RENDER = {
  /** Za touto vzdáleností se chunky vůbec nekreslí. */
  viewDistance: 2400,
  /** Chunky dál než tohle nevrhají stíny (jen je přijímají). */
  shadowCastDistance: 700,
  near: 0.25,
  far: 4200,
  fov: 62,
  /** Horní strop pro device pixel ratio — na 4K displejích jinak zbytečně pálíme fill rate. */
  maxPixelRatio: 1.5,
  /** Expozice před tone mappingem. Preethamova obloha je v HDR velmi jasná. */
  exposure: 0.32,
  /** Hustota exponenciální mlhy. */
  fogDensity: 0.00042,
  /** CSM kaskády. 3 je dobrý poměr kvalita/výkon pro otevřený terén. */
  shadowCascades: 3,
  shadowMapSize: 2048,
  /** Maximální vzdálenost, do které CSM řeší stíny. */
  shadowMaxFar: 900,
} as const;

export const BLOOM = {
  /** Nad tímto jasem začíná bloom. 1.0 = až skutečně přepálené hodnoty. */
  threshold: 1.0,
  smoothing: 0.2,
  intensity: 0.42,
  /** Rozsah rozmazání. Vysoká hodnota udělá ze slunce kouli přes půl obrazu. */
  radius: 0.40,
} as const;

/** Dynamické rozlišení — drží framerate tím, že snižuje render scale. */
export const ADAPTIVE = {
  enabled: true,
  targetFps: 60,
  minScale: 0.62,
  maxScale: 1.0,
  step: 0.06,
  /** Jak často (ms) se render scale přepočítá. */
  interval: 900,
} as const;

export const SUN = {
  /** Elevace slunce ve stupních nad horizontem. */
  elevation: 28,
  /** Azimut ve stupních. */
  azimuth: 145,
  /** Intenzita ambientního světla z oblohy (IBL). Vyšší = plošší, mlhavější obraz. */
  environmentIntensity: 0.62,
  intensity: 4.5,
  /** Úhlová velikost kotouče (metry na vzdálenosti 3000 m). 90 ≈ 1,7°. */
  diskSize: 55,
  /** HDR jas jádra kotouče. Vyšší = víc bloomu kolem slunce. */
  diskBrightness: 14,
  /** Jas měkkého halo kolem kotouče. */
  diskGlow: 0.25,
  /**
   * Škála jasu samotné oblohy. Preethamův model dává v okolí slunce tak vysoké
   * HDR hodnoty, že po ACES vždycky skončí bílá. Tímhle se obloha stáhne,
   * aniž by ztmavl terén — ten se dosvítí environmentIntensity níž.
   */
  skyIntensity: 0.5,
} as const;
